<script setup lang="ts">
  import type { Contact } from '@stuut/api/models';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';

  interface Props {
    contact: Contact;
    showEnableDisable: boolean;
  }

  export interface ContactDropdownEmits {
    (e: 'edit', contact: Contact): void;
    (e: 'delete', contact: Contact): void;
    (e: 'makePrimary', contact: Contact, communicationChannel: string): void;
    (e: 'enableContact', contact: Contact, communicationChannel: string): void;
    (e: 'disableContact', contact: Contact, communicationChannel: string): void;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<ContactDropdownEmits>();
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <slot name="trigger" />
    </DropdownMenuTrigger>

    <DropdownMenuContent class="w-44" align="end">
      <DropdownMenuGroup>
        <DropdownMenuItem
          v-if="!contact.is_primary_email && !contact.is_email_disabled"
          icon
          @click="emits('makePrimary', contact, 'email')"
        >
          <p>Make Primary Email</p>
          <i class="fa far fa-envelope" />
        </DropdownMenuItem>

        <DropdownMenuItem
          v-if="contact?.phone && !contact.is_primary_phone && !contact.is_phone_disabled"
          icon
          @click="emits('makePrimary', contact, 'call')"
        >
          <p>Make Primary Phone</p>
          <i class="fa far fa-phone" />
        </DropdownMenuItem>

        <template v-if="props.showEnableDisable">
          <DropdownMenuItem
            v-if="contact.is_email_disabled"
            icon
            @click="emits('enableContact', contact, 'email')"
          >
            <p>Enable Email</p>
            <i class="fa-solid fa-toggle-on" />
          </DropdownMenuItem>

          <DropdownMenuItem v-else icon @click="emits('disableContact', contact, 'email')">
            <p>Disable Email</p>
            <span class="relative icon-with-slash">
              <i class="fa-regular fa-envelope" />
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            v-if="contact.is_phone_disabled"
            icon
            @click="emits('enableContact', contact, 'call')"
          >
            <p>Enable Phone</p>
            <i class="fa-solid fa-toggle-on" />
          </DropdownMenuItem>

          <DropdownMenuItem v-else icon @click="emits('disableContact', contact, 'call')">
            <p>Disable Phone</p>
            <i class="fa-regular fa-phone-slash" />
          </DropdownMenuItem>

          <DropdownMenuItem
            v-if="contact.isPartnerAccountContact"
            icon
            @click="emits('edit', contact)"
          >
            <p>Edit</p>
            <i class="fa far fa-pencil" />
          </DropdownMenuItem>

          <DropdownMenuItem
            v-else-if="!contact.isPartnerAccountContact"
            icon
            @click="emits('edit', contact)"
          >
            <p>Edit</p>
            <i class="fa far fa-pencil" />
          </DropdownMenuItem>
        </template>

        <DropdownMenuItem
          v-if="!contact.isPartnerAccountContact"
          icon
          @click="emits('delete', contact)"
        >
          <p>Delete</p>
          <i class="fa far fa-trash-alt text-destructive" />
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<style scoped></style>
